import React, {useCallback, useEffect, useState} from 'react'
import Grid from '@mui/material/GridLegacy'
import StyledTextField from '@/components/pages/NewDatasetJobPage/StyledTextField'
import StyledEndButtonContainer from '@/components/common/StyledEndButtonContainer'
import {LoadingButton} from '@mui/lab'
import {Chip, FormControl, IconButton, MenuItem, Select} from '@mui/material'
import {gql, useMutation} from '@apollo/client'
import {useNavigate} from 'react-router-dom'
import {ROUTES} from '@/utils/routes'
import JobErrorModal from '@/components/common/JobErrorModal'
import ArrowBackSharpIcon from '@mui/icons-material/ArrowBackSharp'
import {StyledInputLabel} from '@/components/common/CustomStyledComponents'

type InputType = {
    name: string,
    value: string,
    error: boolean
}

type NewDatabaseInputs = {
    donorsInfoCsvPath: InputType,
    gvcfFolderPath: InputType,
    gvcfFileExtension: InputType,
    refGenome: InputType,
    description: InputType,
    name: InputType,
    databaseVersion: InputType,
    maleStr: InputType,
    femaleStr: InputType,
    genomeStr: InputType,
    exomeStr: InputType,
    excludedPhenoFilterFields: {
        name: string,
        value: string,
        valueArray: string[],
        error: boolean
    },
    annotatorName: InputType
} & Record<string, any>

const NEW_DATABASE_INPUTS_INITIAL_STATE: NewDatabaseInputs = {
    donorsInfoCsvPath: {name: 'donorsInfoCsvPath', value: '', error: false},
    gvcfFolderPath: {name: 'gvcfFolderPath', value: '', error: false},
    gvcfFileExtension: {name: 'gvcfFileExtension', value: '', error: false},
    refGenome: {name: 'refGenome', value: 'GRCh38', error: false},
    description: {name: 'description', value: '', error: false},
    name: {name: 'name', value: '', error: false},
    databaseVersion: {name: 'databaseVersion', value: '', error: false},
    maleStr: {name: 'maleStr', value: '', error: false},
    femaleStr: {name: 'femaleStr', value: '', error: false},
    genomeStr: {name: 'genomeStr', value: '', error: false},
    exomeStr: {name: 'exomeStr', value: '', error: false},
    excludedPhenoFilterFields: {name: 'excludedPhenoFilterFields', value: '', valueArray: ['sample_id'], error: false},
    annotatorName: {name: 'annotatorName', value: 'nirvana', error: false}
}

const REF_GENOME_VALUES = ['GRCh38']
const ANNOTATOR_NAME_VALUES = ['nirvana']

const SUBMIT_NEW_DATABASE = gql`
    mutation SubmitNewDatabase($donorsInfoCsvPath: String!,
        $gvcfFolderPath: String!,
        $gvcfFileExtension: String!,
        $refGenome: String!,
        $description: String!,
        $name: String!,
        $databaseVersion: String!,
        $maleStr: String!,
        $femaleStr: String!,
        $genomeStr: String!,
        $exomeStr: String!,
        $excludedPhenoFilterFields: [String!]!,
        $annotatorName: String!) {
        submitNewDatabase(
            donors_info_csv_path: $donorsInfoCsvPath,
            gvcf_folder_path: $gvcfFolderPath,
            gvcf_file_extension: $gvcfFileExtension,
            ref_genome: $refGenome,
            description: $description,
            name: $name,
            database_version: $databaseVersion,
            male_str: $maleStr,
            female_str: $femaleStr,
            genome_str: $genomeStr,
            exome_str: $exomeStr,
            excluded_pheno_filter_fields: $excludedPhenoFilterFields,
            annotator_name: $annotatorName
        )
    }
`

const NewDatabaseJobPage = () => {
    const [inputs, setInputs] = useState<NewDatabaseInputs>(NEW_DATABASE_INPUTS_INITIAL_STATE)
    const [jobErrorModalOpen, setJobErrorModalOpen] = useState<boolean>(false)

    const [submitNewDbJob, {
        loading: submitNewDbJobLoading,
        error: submitNewDbJobError
    }] = useMutation(SUBMIT_NEW_DATABASE)

    const navigate = useNavigate()

    const handleInputChange = useCallback((property: string, value: any) => {
        setInputs({...inputs, [property]: {...inputs[property], value: value, error: !value}})
    }, [inputs, setInputs])

    const handleExcludedPhenoFilterFields = useCallback((value: any) => {
        setInputs({...inputs, excludedPhenoFilterFields: {...inputs.excludedPhenoFilterFields, value: value}})
    }, [inputs, setInputs])

    const validateExludedPhenoFilterFields = useCallback(() => {
        setInputs({
            ...inputs,
            excludedPhenoFilterFields: {
                ...inputs.excludedPhenoFilterFields,
                error: inputs.excludedPhenoFilterFields.valueArray.length < 1
            }
        })
    }, [inputs, setInputs])

    const handleExcludedPhenoFilterChipDelete = useCallback((chipValue: string) => {
        setInputs((prev: NewDatabaseInputs) => ({
            ...prev, excludedPhenoFilterFields: {
                ...prev.excludedPhenoFilterFields,
                valueArray: [...prev.excludedPhenoFilterFields.valueArray.filter(val => val !== chipValue)]
            }
        }))
    }, [setInputs])

    const handleExludedPhenoFilterKeyUp = useCallback((key: string) => {
        if (key === 'Enter') {
            let newValueArr = [...inputs.excludedPhenoFilterFields.valueArray]
            const value = inputs.excludedPhenoFilterFields.value
            if(value.includes(';')) {
                newValueArr = newValueArr.concat(value.split(';'))
            } else {
                newValueArr.push(inputs.excludedPhenoFilterFields.value)
            }
            setInputs({
                ...inputs,
                excludedPhenoFilterFields: {
                    ...inputs.excludedPhenoFilterFields,
                    value: '',
                    valueArray: newValueArr
                }
            })
        }
    }, [inputs, setInputs])

    const handleNewDbJobSubmit = useCallback(async () => {
        let formValid = true
        const validatedInput = {...inputs}
        for (const prop in inputs) {
            if (prop !== inputs.excludedPhenoFilterFields.name) {
                validatedInput[prop] = {...inputs[prop], error: !inputs[prop].value}
            } else {
                validatedInput.excludedPhenoFilterFields = {
                    ...inputs.excludedPhenoFilterFields,
                    error: inputs.excludedPhenoFilterFields.valueArray.length < 1
                }
            }

            if (formValid && validatedInput[prop].error) {
                formValid = false
            }
        }

        if (!formValid) {
            setInputs(validatedInput)
            return
        }

        await submitNewDbJob({
            variables: {
                donorsInfoCsvPath: validatedInput.donorsInfoCsvPath.value,
                gvcfFolderPath: validatedInput.gvcfFolderPath.value,
                gvcfFileExtension: validatedInput.gvcfFileExtension.value,
                refGenome: validatedInput.refGenome.value,
                description: validatedInput.description.value,
                name: validatedInput.name.value,
                databaseVersion: validatedInput.databaseVersion.value,
                maleStr: validatedInput.maleStr.value,
                femaleStr: validatedInput.femaleStr.value,
                genomeStr: validatedInput.genomeStr.value,
                exomeStr: validatedInput.exomeStr.value,
                excludedPhenoFilterFields: validatedInput.excludedPhenoFilterFields.valueArray,
                annotatorName: validatedInput.annotatorName.value
            }
        })

        navigate(ROUTES.JOBS_DATABASE)
    }, [inputs, setInputs])

    useEffect(() => {
        if (submitNewDbJobError) {
            setJobErrorModalOpen(true)
        }
    }, [submitNewDbJobError])

    return <>
        <Grid container spacing={2} marginY={2}>
            <Grid item xs={12} paddingTop="0 !important;">
                <IconButton size="small" color="primary" onClick={() => navigate(ROUTES.JOBS_DATABASE)}>
                    <ArrowBackSharpIcon/>
                </IconButton>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
                <StyledTextField
                    id="donorsInfoCsvPath"
                    label="Donors info csv path"
                    size="small"
                    required
                    value={inputs.donorsInfoCsvPath.value}
                    error={inputs.donorsInfoCsvPath.error}
                    onChange={(evt: any) => handleInputChange(inputs.donorsInfoCsvPath.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
                <StyledTextField
                    id="gvcfFolderPath"
                    label="GVCF folder path"
                    size="small"
                    required
                    value={inputs.gvcfFolderPath.value}
                    error={inputs.gvcfFolderPath.error}
                    onChange={(evt: any) => handleInputChange(inputs.gvcfFolderPath.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <StyledTextField
                    id="gvcfFileExtension"
                    label="GVCF file extension"
                    size="small"
                    required
                    value={inputs.gvcfFileExtension.value}
                    error={inputs.gvcfFileExtension.error}
                    onChange={(evt: any) => handleInputChange(inputs.gvcfFileExtension.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <FormControl fullWidth size="small" required>
                    <StyledInputLabel id="refGenomeLabel" error={false}>Ref genome</StyledInputLabel>
                    <Select
                        id="refGenome"
                        labelId="refGenomeLabel"
                        label="Ref genome"
                        variant="outlined"
                        size="small"
                        required
                        value={inputs.refGenome.value}
                        error={inputs.refGenome.error}
                        onChange={(evt) => handleInputChange(inputs.refGenome.name, evt.target.value)}
                    >
                        {REF_GENOME_VALUES.map((value) => (
                            <MenuItem key={'key-' + value} value={value}>
                                {value}
                            </MenuItem>))
                        }
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <StyledTextField
                    id="databaseVersion"
                    label="Database version"
                    size="small"
                    required
                    value={inputs.databaseVersion.value}
                    error={inputs.databaseVersion.error}
                    onChange={(evt: any) => handleInputChange(inputs.databaseVersion.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <FormControl fullWidth size="small" required>
                    <StyledInputLabel id="annotatorNameLabel" error={false}>Annotator name</StyledInputLabel>
                    <Select
                        id="annotatorName"
                        labelId="annotatorNameLabel"
                        label="Annotator name"
                        variant="outlined"
                        size="small"
                        required
                        value={inputs.annotatorName.value}
                        error={inputs.annotatorName.error}
                        onChange={(evt) => handleInputChange(inputs.annotatorName.name, evt.target.value)}
                    >
                        {ANNOTATOR_NAME_VALUES.map((value) => (
                            <MenuItem key={'key-' + value} value={value}>
                                {value}
                            </MenuItem>))
                        }
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
                <StyledTextField
                    id="name"
                    label="Name"
                    size="small"
                    required
                    value={inputs.name.value}
                    error={inputs.name.error}
                    onChange={(evt: any) => handleInputChange(inputs.name.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
                <StyledTextField
                    id="description"
                    label="Description"
                    size="small"
                    required
                    value={inputs.description.value}
                    error={inputs.description.error}
                    onChange={(evt: any) => handleInputChange(inputs.description.name, evt.target.value)}
                    fullWidth
                    multiline/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <StyledTextField
                    id="maleStr"
                    label="Male str"
                    size="small"
                    required
                    value={inputs.maleStr.value}
                    error={inputs.maleStr.error}
                    onChange={(evt: any) => handleInputChange(inputs.maleStr.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <StyledTextField
                    id="femaleStr"
                    label="Female str"
                    size="small"
                    required
                    value={inputs.femaleStr.value}
                    error={inputs.femaleStr.error}
                    onChange={(evt: any) => handleInputChange(inputs.femaleStr.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <StyledTextField
                    id="genomeStr"
                    label="Genome str"
                    size="small"
                    required
                    value={inputs.genomeStr.value}
                    error={inputs.genomeStr.error}
                    onChange={(evt: any) => handleInputChange(inputs.genomeStr.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
                <StyledTextField
                    id="exomeStr"
                    label="Exome str"
                    size="small"
                    required
                    value={inputs.exomeStr.value}
                    error={inputs.exomeStr.error}
                    onChange={(evt: any) => handleInputChange(inputs.exomeStr.name, evt.target.value)}
                    fullWidth/>
            </Grid>
            <Grid item xs={12}>
                <Grid container columnSpacing={0.5} rowSpacing={1}>
                    {inputs.excludedPhenoFilterFields.valueArray.map((exludedPhenoFilter, idx) => (
                        <Grid item key={exludedPhenoFilter + '-' + idx}>
                            <Chip
                                label={'"' + exludedPhenoFilter + '"'}
                                variant="outlined"
                                onDelete={() => handleExcludedPhenoFilterChipDelete(exludedPhenoFilter)}/>
                        </Grid>
                    ))}
                </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
                <StyledTextField
                    id="excludedPhenoFilterFields"
                    label="Excluded pheno filter fields"
                    size="small"
                    required
                    fullWidth
                    value={inputs.excludedPhenoFilterFields.value}
                    error={inputs.excludedPhenoFilterFields.error}
                    onChange={(evt: any) => handleExcludedPhenoFilterFields(evt.target.value)}
                    onBlur={validateExludedPhenoFilterFields}
                    onKeyUp={(evt: any) => handleExludedPhenoFilterKeyUp(evt.key)}
                    helperText="Press enter to confirm input. You can also use semicolon ; as delimiter for multi input."/>
            </Grid>
            <StyledEndButtonContainer>
                <LoadingButton
                    disabled={submitNewDbJobLoading}
                    loading={submitNewDbJobLoading}
                    variant="contained"
                    color="primary"
                    onClick={handleNewDbJobSubmit}>
                    Create new Job
                </LoadingButton>
            </StyledEndButtonContainer>
        </Grid>
        <JobErrorModal isOpen={jobErrorModalOpen} message="Unable to create new job"
            onClose={() => setJobErrorModalOpen(false)}/>
    </>
}

export default NewDatabaseJobPage